import {
  KatanaPerpsVaultDeposit,
  KatanaPerpsVaultWithdrawal,
} from '@katanaperps/katana-perps-sdk';
import {
  keepPreviousData,
  skipToken,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { getChainPrivateApi } from '#/api/apiCoreSdk';
import { addTypeToDepositWithdraw } from '#/api/apiExchange';
import { KatanaApiError } from '#/api/apiPatchConstants';
import { ApiBalanceHistory } from '#/api/types';
import { useWebsocketEventListener } from '#/components/hooks';
import {
  ApiVault,
  ApiVaultDeposit,
  ApiVaultWithdrawal,
} from '#/components/vaults/shared/types';
import { getPaginationPerPageLimit } from '#/config/configUtils';
import {
  PAGINATION_PAGES_LIMIT,
  VAULTS_REFRESH_SETTLE_DELAY_MS,
} from '#/config/constants';
import useEventCallback from '#/hooks/useEventCallback';
import { waitAsync } from '#/lib/promiseTools';

import { minutes, seconds } from './utils';
import { useInvalidateAllOnChainVaultsQueries } from './vaultsOnChainQueries';

export function useInvalidateAllVaultsQueries() {
  const queryClient = useQueryClient();
  const invalidate = useEventCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ['vaults'],
    });
  });
  const invalidateOnChain = useInvalidateAllOnChainVaultsQueries();
  const invalidateAllQueries = useEventCallback(async () => {
    await Promise.allSettled([invalidate(), invalidateOnChain()]);
  });
  return invalidateAllQueries;
}

export const useGlobalVaultQueriesInvalidator = () => {
  const invalidateVaults = useInvalidateAllVaultsQueries();

  /**
   * Invalidate immediately (API data is usually fresh at the websocket event
   * moment) and once more after a settle delay - refetching on-chain data at
   * the event moment races the chain state / indexers and can still return
   * pre-transaction values (eg. depositor owedQuantity of 0 right after the
   * first deposit)
   */
  const invalidateVaultsNowAndSettled = useEventCallback(() => {
    invalidateVaults();
    waitAsync(VAULTS_REFRESH_SETTLE_DELAY_MS).then(invalidateVaults);
  });

  useWebsocketEventListener('deposits', _message => {
    // Invalidate vaults and all depositorBalanceHistory
    invalidateVaultsNowAndSettled();
  });
  useWebsocketEventListener('withdrawals', _message => {
    invalidateVaultsNowAndSettled();
  });
  useWebsocketEventListener('webclient', message => {
    if (
      message.data.event === 'transaction_settled' &&
      message.data.action === 'withdraw'
    ) {
      invalidateVaultsNowAndSettled();
    }
  });
};

/**
 * Liquidated/Exited vaults still report stale apy/tvl/collateralization figures from
 * the API. Blank them so the UI shows dashes and `Number(null) === 0` makes
 * table sorting place liquidated vaults at the bottom.
 */
export function nullifyLiquidatedAndExitedVaultFields(
  vault: ApiVault
): ApiVault {
  if (vault.liquidatedTime) {
    return {
      ...vault,
      apy: null,
      apyFixed: null,
      apyFarm: null,
      tvl: null,
      collateralization: null,
    };
  }
  if (vault.exitedTime) {
    return {
      ...vault,
      apy: null,
      apyFixed: null,
      apyFarm: null,
    };
  }
  return vault;
}

export function useVaultsQuery() {
  return useQuery({
    queryKey: ['vaults', 'list'],
    queryFn: () =>
      getChainPrivateApi()
        .getInternalVaults({})
        .then(response => ({
          ...response,
          vaults: response.vaults.map(nullifyLiquidatedAndExitedVaultFields),
        })),
    staleTime: seconds(15),
    refetchInterval: minutes(3),
  });
}

export function useVaultQuery({ managerWallet }: { managerWallet: string }) {
  return useQuery({
    queryKey: ['vaults', 'single', managerWallet],
    queryFn: () =>
      getChainPrivateApi()
        .getInternalVault({ manager: managerWallet })
        .then(nullifyLiquidatedAndExitedVaultFields),
    staleTime: seconds(10),
    refetchInterval: minutes(2),
    retry: (failureCount, error: KatanaApiError) => {
      // Don't retry on 404 errors (Vault might not exist)
      if (error?.idexErrorCode === 'MANAGED_ACCOUNT_NOT_FOUND') {
        return false;
      }
      // Default retry behavior for other errors (3 retries)
      return failureCount < 3;
    },
  });
}

export function useVaultDepositorsQuery({
  managerWallet,
}: {
  managerWallet: string;
}) {
  return useQuery({
    queryKey: ['vaults', 'depositors', managerWallet],
    queryFn: () =>
      getChainPrivateApi().getInternalVaultDepositors({
        manager: managerWallet,
      }),
    staleTime: seconds(15),
    refetchInterval: minutes(5),
  });
}

/** Improve typings in the response by adding extra `type` field */
function addTypeToVaultDepositWithdraw(
  items: (KatanaPerpsVaultDeposit | KatanaPerpsVaultWithdrawal)[]
) {
  return items.map(item => {
    if ('depositId' in item) {
      const deposit: ApiVaultDeposit = item;
      return { ...deposit, type: 'deposit' as const };
    }
    if ('withdrawalId' in item) {
      const withdrawal: ApiVaultWithdrawal = item;
      return { ...withdrawal, type: 'withdrawal' as const };
    }
    // Unreachable
    throw new Error('Unknown vault DepositWithdrawal type');
  });
}

export const VAULTS_BALANCE_HISTORY_PAGE_LIMIT = getPaginationPerPageLimit(50);

export function useVaultBalanceHistoryQuery({
  enabled,
  managerWallet,
  limit,
  page,
}: {
  enabled: boolean;
  managerWallet: string;
  limit: number;
  page: number;
}) {
  return useQuery({
    queryKey: ['vaults', 'balanceHistory', managerWallet, limit, page],
    queryFn:
      page > PAGINATION_PAGES_LIMIT
        ? skipToken
        : () =>
            getChainPrivateApi()
              .getInternalVaultBalanceHistory({
                limit,
                manager: managerWallet,
                page,
              })
              .then(response => ({
                ...response,
                items: addTypeToVaultDepositWithdraw(response.items),
              })),
    enabled,
    staleTime: seconds(10),
    refetchInterval: minutes(5),
    // Required nice UX on pagination
    placeholderData: keepPreviousData,
  });
}

export function useVaultDepositorBalanceHistoryQuery({
  enabled,
  managerWallet,
  limit,
  page,
  walletAddress,
}: {
  enabled: boolean;
  managerWallet: string;
  limit: number;
  page: number;
  walletAddress: string;
}) {
  return useQuery<ApiBalanceHistory>({
    queryKey: [
      'vaults',
      'depositorBalanceHistory',
      managerWallet,
      walletAddress,
      limit,
      page,
    ],
    queryFn:
      page > PAGINATION_PAGES_LIMIT
        ? skipToken
        : () =>
            getChainPrivateApi()
              .getInternalVaultDepositorBalanceHistory({
                depositor: walletAddress,
                limit,
                manager: managerWallet,
                page,
              })
              .then(response => ({
                count: response.count,
                items: response.items.map(addTypeToDepositWithdraw),
              })),
    enabled,
    staleTime: seconds(10),
    refetchInterval: minutes(5),
    // Required nice UX on pagination
    placeholderData: keepPreviousData,
  });
}

// TODO: vaults Remove once not needed ?
// export function useVaultFarmEarningsQuery({
//   managerWallet,
//   walletAddress,
// }: {
//   managerWallet: string;
//   walletAddress: string;
// }) {
//   return useQuery({
//     queryKey: ['vaults', 'farmEarnings', walletAddress, managerWallet],
//     enabled: !!walletAddress,
//     queryFn: () =>
//       getChainPrivateApi().getInternalVaultFarmEarnings({
//         depositor: walletAddress,
//         manager: managerWallet,
//       }),
//     staleTime: seconds(10),
//     refetchInterval: minutes(3),
//   });
// }
